// TO DO => extract to diff files
// TO DO => maybe better naming ?

import { featureFlags } from "@/config/featureFlags";
import type { SubscriptionPlan } from "@/server/types/subscription";
import type { AttachmentKind } from "@/utils/attachmentType";

export const MB = 1024 * 1024; // 1MB

// Support contact address surfaced in recovery flows (e.g. forgot-username).
// Single source of truth so the "Contact us" links can't drift.
export const SUPPORT_EMAIL = "support@orionmessenger.io";

export const BE_MAX_LIMIT_MESSAGES_PER_REQUEST = 100;
export const MEMBERS_LIST_PAGE_SIZE = 50;
export const MESSAGE_LIST_PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_TIME = 500;
export const CHAT_LIST_PAGE_SIZE = 50;
export const MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD = 50;
// Legacy flat ceilings. These are NO LONGER the enforced caps — enforcement is
// the per-plan ATTACHMENT_LIMITS matrix below, resolved in
// utils/attachmentSizeLimit.ts. They survive only as reference values: 100 MB
// is the proven single-blob upload ceiling, and MAX_IMAGE_FILE_SIZE (40 MB) is
// the old flat image cap the Architect ladder's "tightening" test measures
// against. Not read on any live send path.
export const MAX_DOC_FILE_SIZE = 100 * MB;
// CSK rotation is O(members) work (a new-CSK encapsulation per member), so it is
// limited to groups at or below this size. Larger groups keep the static epoch-0
// CSK (the pre-rotation behavior) and never rotate.
export const MAX_ROTATION_MEMBER_COUNT = 1000;
// P2P forward secrecy: rotate the CSK once this many messages have been
// exchanged at the current epoch. Groups rotate on membership change instead;
// P2P chats have no membership events, so they advance the epoch by volume.
export const P2P_ROTATION_MESSAGE_INTERVAL = 50;
export const MAX_IMAGE_FILE_SIZE = 40 * MB;
// Videos at/above this size take the chunked upload + streaming path
// (encrypted 5 MB parts, streamed via the service worker). Shared by the send
// flow (messages.ts) and by UI that needs to know a staged file is "large".
export const LARGE_ATTACHMENT_THRESHOLD = 50 * MB;
// Staged videos below this size autoplay (muted, looping) in the message
// input; at/above it the input shows a static thumbnail instead. A UX/battery
// knob, deliberately independent from the upload-path threshold above —
// autoplaying a big file makes the media engine read it end-to-end on repeat
// before it's even sent.
export const MAX_AUTOPLAY_PREVIEW_SIZE = 20 * MB;

// ─── Attachment size limits: the scope × file-kind × plan matrix ────────────
// The knobs for size-gating attachments. Data only — resolution lives in
// `utils/attachmentSizeLimit.ts`, the one place that decides which cell applies
// and clamps it to what the transport can actually carry. See
// docs/attachment-size-limits.md.
//
// Client-side only, and therefore ADVISORY: the plan is read from persisted
// subscription state, so these shape the UX and cannot enforce entitlements.
// The server must enforce the same ladders independently (a tracked backend
// action — see the doc).
//
// The dimensions:
//   scope  — "architect" (a chat with ORION Architect) | "regular" (everything
//            else). Binary today; add a member here + a matrix branch to grow.
//   kind   — pdf | image | video (utils/attachmentType.ts). Voice is absent
//            everywhere: it is duration-gated, never size-gated.
//   plan   — the subscription tier.
//
// A missing (scope, kind) cell means the kind is NOT OFFERED in that scope and
// is rejected outright. That is deliberate and does two jobs: it BLOCKS video
// in Architect chats (OA cannot process it), and it is fail-closed for any
// future kind nobody has set a number for.

export type ChatScope = "architect" | "regular";

// Per-plan Architect PDF ceiling. The subscription table's "PDF attachment
// limit" row renders FROM this map rather than repeating the numbers —
// otherwise the two drift, which is how the table came to advertise a ladder
// nothing enforced.
//
// The OA agent has its own ceilings in `oa-agent/src/ai/attachments/constants.ts`:
// MAX_PDF_SIZE 100 MB (matches this ladder's premium cap) and MAX_IMAGE_SIZE
// 5 MB — but images over that are AUTO-RESIZED, not declined, so the advertised
// 10 MB image tiers are safe. No agent change is needed for these numbers; see
// docs/attachment-size-limits.md §5.
export const ARCHITECT_PDF_LIMITS: Record<SubscriptionPlan, number> = {
  free: 100 * 1024,
  standard: 1 * MB,
  pro: 10 * MB,
  proplus: 20 * MB,
  premium: 100 * MB,
};

// Per-plan Architect image ceiling. Same contract as the PDF ladder: the
// table's "Image sharing limit" row renders FROM this map. A TIGHTENING for
// every plan versus the old flat 40 MB; the agent itself accepts only 5 MB.
export const ARCHITECT_IMAGE_LIMITS: Record<SubscriptionPlan, number> = {
  free: 5 * MB,
  standard: 10 * MB,
  pro: 10 * MB,
  proplus: 10 * MB,
  premium: 10 * MB,
};

// Regular-chat PDF & image share ONE ladder — the numbers the plans page
// advertises. Paid tiers sit at 100 MB: single-blob transport can't carry more
// (the backend caps at 100,000,000 bytes, so a full 100 MiB actually needs the
// backend cap raised — a tracked action, see the doc), and there is no product
// reason to split PDF from image here.
export const REGULAR_DOC_IMAGE_LIMITS: Record<SubscriptionPlan, number> = {
  free: 10 * MB,
  standard: 100 * MB,
  pro: 100 * MB,
  proplus: 100 * MB,
  premium: 100 * MB,
};

// Regular-chat video ladder — the only place the big headroom lives. Above
// 100 MB is real ONLY with chunked upload on; the resolver clamps each cell to
// MAX_VIDEO_FILE_SIZE (which already encodes the flag: 1000 MB on, 100 MB off),
// so with the flag off every paid tier effectively caps at 100 MB.
export const REGULAR_VIDEO_LIMITS: Record<SubscriptionPlan, number> = {
  free: 10 * MB,
  standard: 100 * MB,
  pro: 200 * MB,
  proplus: 400 * MB,
  premium: 1000 * MB,
};

// The Architect sub-map, kept as its own named export so the matrix reads
// symmetrically and the config test can assert its shape directly. (The
// plans-page rows derive from the ladders — ARCHITECT_PDF_LIMITS /
// ARCHITECT_IMAGE_LIMITS — not from this map.)
export const ARCHITECT_ATTACHMENT_LIMITS: Partial<
  Record<AttachmentKind, Record<SubscriptionPlan, number>>
> = {
  pdf: ARCHITECT_PDF_LIMITS,
  image: ARCHITECT_IMAGE_LIMITS,
  // video: intentionally absent — OA can't process video, so it is blocked in
  // Architect chats. When/if that changes it will NOT inherit the regular 1 GB
  // ladder; add an explicit cell with its own numbers.
};

// The matrix. Every (scope, kind, plan) cell is independently tunable; an
// absent (scope, kind) is "not offered here" (see the header note).
export const ATTACHMENT_LIMITS: Record<
  ChatScope,
  Partial<Record<AttachmentKind, Record<SubscriptionPlan, number>>>
> = {
  architect: ARCHITECT_ATTACHMENT_LIMITS,
  regular: {
    pdf: REGULAR_DOC_IMAGE_LIMITS,
    image: REGULAR_DOC_IMAGE_LIMITS,
    video: REGULAR_VIDEO_LIMITS,
  },
};

// TWO jobs, deliberately on ONE number — read both before tuning it.
//
// 1. PREVIEW CEILING. The largest PDF the in-app viewer will render; above it
//    the message card downloads to disk instead. Why: pdf.js receives a `blob:`
//    URL, which cannot serve range requests (`isValidFetchUrl` accepts only
//    http/https), so it must buffer the WHOLE document before painting page 1,
//    then rasterise pages to canvas on top of an already-large whole-file
//    decrypt. Keeping big PDFs out of the parser is also less untrusted input
//    through a historically CVE-prone component (see isEvalSupported in
//    PdfViewer).
//
// 2. LOCAL-COPY CEILING. The largest document we keep our own cached copy of
//    (processFileAttachment). Above it, a file can only ever be saved to disk,
//    so a copy would speed up at most one repeat download — while costing its
//    full size in a storage quota shared with every other attachment, and
//    pushing the eviction sweep against the user's other files.
//
// ONE number on purpose. Two numbers would let preview exceed cache, creating
// files that open in-app but are never kept — the worst case, paying a full
// download + decrypt + parse on EVERY open. One value makes that unreachable.
// Raising this therefore also raises what we store; that is intended, not a
// side effect.
//
// CURRENTLY INERT, deliberately. This equals both MAX_DOC_FILE_SIZE and
// ARCHITECT_PDF_LIMITS.premium, and every comparison against it is `<=`, so no
// PDF the client will send can exceed it: nothing demotes to download and
// nothing skips the cache. The routing, the download glyph and the cache skip
// are all in place and tested, waiting on a number. Lowering this is what
// switches them on — the backend's own 100,000,000-byte cap puts the real
// ceiling near 95.4 MiB, so that is the upper bound worth considering.
export const PDF_PREVIEW_MAX_BYTES = 100 * MB;

// Per-type ceilings for media auto-download (opt-in; see MediaAutoDownloadState).
// Baked here rather than a user-facing selector — sensible fixed caps keep the
// settings simple and prevent a user from enabling auto-download of huge files.
// A file above its cap simply waits for a tap. Videos above LARGE_ATTACHMENT_
// THRESHOLD never auto-download at all (they stream via the SW).
export const AUTO_DOWNLOAD_MAX_BYTES = {
  image: 5 * MB,
  file: 5 * MB,
  video: 20 * MB,
} as const;
// Cap on concurrent auto-downloads so a media-heavy chat (or a burst of arrivals)
// can't fire dozens of simultaneous fetch+decrypt operations. Excess queue and
// run as slots free (see autoDownloadQueue). Sibling of the upload cap below.
export const MAX_PARALLEL_AUTO_DOWNLOADS = 3;
// At most this many chunked (large-video) upload sessions per client at once.
// v1 is a client-side gate (block + toast at send time); the server-side 429
// backstop is what makes the cap global across tabs/devices — see the
// session-cap section of docs/chunked-video-upload.md. Env-overridable for
// ops tuning; anything unparseable or < 1 falls back to the default.
const RAW_SESSION_CAP = Number(
  import.meta.env.VITE_MAX_PARALLEL_CHUNKED_UPLOADS,
);
export const MAX_PARALLEL_CHUNKED_UPLOADS =
  Number.isFinite(RAW_SESSION_CAP) && RAW_SESSION_CAP >= 1
    ? Math.floor(RAW_SESSION_CAP)
    : 3;
// At most this many single-blob attachment sends (documents / images / voice /
// edit / forward) may encrypt+upload at once, per tab. Documents/images can be
// ~100 MB and each send transiently costs ~3x its size while encrypting, so the
// cap bounds the peak that OOMs a tab under a burst of sends. Sibling of the
// receive-side MAX_PARALLEL_AUTO_DOWNLOADS. QUEUE, don't reject (see blobSendQueue).
// 2 → worst case ~2 x 3 x 100 MiB ≈ 630 MB transient; 3 gets too close to an iOS
// tab ceiling. Distinct from the chunked-video cap above (different path).
export const MAX_PARALLEL_BLOB_SENDS = 2;
// Sends below this size skip the concurrency cap (they still hold a liveness
// beacon). The cap exists to bound the OOM peak from big encrypts; a small send
// (voice note, small image) costs little heap, so making it wait behind two large
// sends is pure latency with no memory benefit. 5 MiB ≈ ~15 MB transient at ~3x —
// negligible even several at once.
export const BLOB_SEND_QUEUE_MIN_BYTES = 5 * 1024 * 1024;
// Attachment upload timeouts (see postAttachment). Idle timer during the transfer
// phase; a separate, longer deadline once the body is fully sent so the server's
// write-through of a large body is not mistaken for a stall (which would abort a
// request the server may complete → an orphaned attachment).
export const UPLOAD_STALL_TIMEOUT_MS = 60_000;
export const UPLOAD_RESPONSE_TIMEOUT_MS = 120_000;
// The stranded-send reconciliation runs once at boot; its guard is burned on
// first run. On the hardened iOS-PWA IDB open-recovery path the DB can be
// momentarily closed, so its query rejects. Retry it a few times (rather than
// silently no-op'ing for the whole session) so it still runs once the DB
// recovers. ~5s window covers a typical recovery; if the DB never opens we give
// up and the next real reload reattempts.
export const STRANDED_RECONCILE_QUERY_ATTEMPTS = 10;
export const STRANDED_RECONCILE_QUERY_RETRY_MS = 500;
// Stranded-send server confirmation (#1177). A non-chunked send stranded on
// PENDING with no server id is confirmed against the server by `client_id`
// before being failed, instead of guessed from `id === _clientId`. These bound
// the per-boot lookup cost: each (chat, thread) group is paged newest-first up
// to MAX_PAGES × PAGE_SIZE messages, and at most MAX_GROUPS groups are probed
// per boot (overflow groups stay PENDING and are retried on the next boot).
export const STRANDED_CONFIRM_PAGE_SIZE = 50;
export const STRANDED_CONFIRM_MAX_PAGES = 3;
export const STRANDED_CONFIRM_MAX_GROUPS = 25;
// Clock-skew tolerance when deciding a send is CONFIRMED absent. A stranded send
// is recent, so the lookup pages newest-first only until it has covered the
// send's own creation time minus this margin — then absence is conclusive even
// in a huge chat (no need to page to the chat's oldest message). Generous vs.
// real client/server clock skew (usually seconds).
export const STRANDED_CONFIRM_SKEW_MARGIN_MS = 5 * 60 * 1000;
// Per-request bound on each confirm lookup. The shared axios client sets no
// `timeout`, so a dead-but-open connection (mobile Wi-Fi→cellular hand-off,
// captive portal) would otherwise hang the whole boot sweep forever — the exact
// eternal-spinner symptom this fix exists to end. On timeout the request aborts
// and the lookup degrades to `unconfirmed` (safe); the next boot retries.
export const STRANDED_CONFIRM_REQUEST_TIMEOUT_MS = 15_000;
// Large videos (>100 MB) only upload reliably via the chunked-upload path.
// When that feature is disabled the single-blob path can't handle them, so we
// keep the old 100 MB ceiling — otherwise oversized videos pass the size check
// silently and fail mid-upload with no "too large" toast.
export const MAX_VIDEO_FILE_SIZE = featureFlags.chunkedUpload
  ? 1000 * MB
  : 100 * MB;
export const MAX_AVATAR_SIZE_MB = 10 * MB;
export const AVATAR_SIZE = 500;

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
  "video/mpeg",
  "video/3gpp",
  "video/x-ms-wmv",
  "video/mov",
  "video/x-m4v",
  "video/*", // Fallback to ensure all video types are accepted
];

export const ALLOWED_DOC_TYPES = ["application/pdf"];

export const ALLOWED_MEDIA_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
];

export const ALL_ALLOWED_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_DOC_TYPES,
];

// TTLs
export const MESSAGE_EDIT_TTL = Infinity; // min

export const ALLOWED_REACTIONS = ["❤️", "👍", "👏", "🔥", "🤣"];

export const BLACK_SVG_200_200 = `
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
  <rect width="200" height="200" fill="black" />
</svg>
`;

export const EMPTY_PLACEHOLDER_FILE = new File(
  [new Blob([BLACK_SVG_200_200], { type: "image/svg+xml" })],
  "black-200x200-placeholder.svg",
  { type: "image/svg+xml" },
);

export const JUMP_TO_MESSAGE_HIGHLIGHT_DURATION = 4000;
export const JUMP_TO_MESSAGE_HIGHLIGHT_COLOR = "rgba(119, 119, 119, 0.2)";
export const LINK_CLASS =
  "text-foreground hover:text-foreground underline cursor-pointer";

// Default date for new chat synchronization
export const DEFAULT_SYNC_DATE = new Date("2020-01-01").toISOString();

// Number of chats fetched in a full background sync (socket connect / post-registration)
export const CHAT_SYNC_FULL_TAKE = 700;

export const FILE_SKELETON_ITEMS_COUNT = 5;
export const LINKS_SKELETON_ITEMS_COUNT = 3;
export const MEDIA_SKELETON_ITEMS_COUNT = 9;

export const PHRASE_ENTROPY = 128;
export const PHRASE_WORDS_COUNT = (PHRASE_ENTROPY + PHRASE_ENTROPY / 32) / 11;
export const MAX_PASSWORD_LENGTH = 50;
export const MAX_WORD_LENGTH = 30;
export const RECOVERY_DELAY_KEYS = {
  STORAGE_KEY: "recoveryKeyLastShown",
  DELAY_PERIOD: 14 * 24 * 60 * 60 * 1000,
  INITIAL_SHOW_DELAY: 5000,
} as const;

// AI Chat Configuration
export const CHAT_HUB_ID = (() => {
  const hubId = import.meta.env.VITE_CHAT_HUB_ID;
  if (!hubId) {
    throw new Error(
      "VITE_CHAT_HUB_ID environment variable is required for AI chat functionality",
    );
  }
  return hubId;
})();

// Username of the OA bot account. When this user is among the typers in a chat,
// the typing indicator renders "Responding..." instead of "is typing...".
//
// The bot's account username IS its hub id per deployment (oa-agent's
// BOT_USERNAME == HUB_ID; e.g. `theoa` in prod/beta/staging, `oa-agent` in
// stage-om). Deriving it from VITE_CHAT_HUB_ID keeps a single source of truth
// and ensures the "Responding..." label resolves correctly in every deployment
// rather than hardcoding one environment's value.
export const OA_BOT_USERNAME = CHAT_HUB_ID;

// Typing indicator timing (the ephemeral receiver/sender contract):
// - Sender re-emits typing=true every 8s while still composing (keep-alive).
// - Receiver expires a typing entry after 10s if not refreshed.
// The 2s gap absorbs network jitter so an actively-typing user never flickers.
export const TYPING_KEEPALIVE_MS = 8000;
export const TYPING_EXPIRY_MS = 10000;
